/**
 * ============================================================================
 * wheelSpinService.ts - DAILY WHEEL SPIN SERVICE
 * ============================================================================
 * 
 * This service manages the daily wheel spin bonus feature.
 * Users get one free spin per day to win PlayCoins or other prizes.
 * 
 * Features:
 * - One free spin per day
 * - Weighted prize distribution
 * - Streak bonuses for consecutive daily spins
 * - VIP multipliers
 * 
 * ============================================================================
 */

import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

// ============================================================================
// TYPES
// ============================================================================

export interface WheelPrize {
  id: string;
  label: string;
  value: number;
  type: 'playcoins' | 'pkcash' | 'multiplier' | 'freebie' | 'jackpot';
  color: string;
  weight: number; // Higher = more likely
  emoji: string;
}

export interface SpinResult {
  success: boolean;
  prize: WheelPrize | null;
  finalValue: number;
  streakBonus: number;
  vipMultiplier: number;
  message: string;
  nextSpinTime: number | null;
  error?: string;
}

export interface SpinStatus {
  canSpin: boolean;
  lastSpinTime: number | null;
  nextSpinTime: number | null;
  currentStreak: number;
  totalSpins: number;
  totalWon: number;
}

// ============================================================================
// WHEEL CONFIGURATION
// ============================================================================

export const WHEEL_PRIZES: WheelPrize[] = [
  // ⚠️ MUST STAY IN LOCKSTEP WITH THE SERVER'S WHEEL_PRIZES (same ids, same
  // order): the landing animation maps prize id -> index, so any drift here
  // parks the pointer on a different segment than the one that paid out.
  { id: 'coins_100', label: '100', value: 100, type: 'playcoins', color: '#3b82f6', weight: 22, emoji: '🪙' },
  { id: 'coins_250', label: '250', value: 250, type: 'playcoins', color: '#22c55e', weight: 18, emoji: '🪙' },
  { id: 'pss_1', label: '1 PSS', value: 1, type: 'pkcash', color: '#06b6d4', weight: 2, emoji: '🪙' },
  { id: 'coins_500', label: '500', value: 500, type: 'playcoins', color: '#8b5cf6', weight: 16, emoji: '💰' },
  { id: 'coins_750', label: '750', value: 750, type: 'playcoins', color: '#14b8a6', weight: 11, emoji: '💰' },
  { id: 'coins_1000', label: '1K', value: 1000, type: 'playcoins', color: '#f59e0b', weight: 13, emoji: '💰' },
  { id: 'pss_3', label: '3 PSS', value: 3, type: 'pkcash', color: '#0ea5e9', weight: 1, emoji: '🪙' },
  { id: 'coins_2500', label: '2.5K', value: 2500, type: 'playcoins', color: '#ec4899', weight: 9, emoji: '💎' },
  { id: 'coins_5000', label: '5K', value: 5000, type: 'playcoins', color: '#ef4444', weight: 6, emoji: '💎' },
  { id: 'jackpot', label: '25K', value: 25000, type: 'jackpot', color: '#fbbf24', weight: 2, emoji: '⭐' },
];

// Calculate total weight for probability
const TOTAL_WEIGHT = WHEEL_PRIZES.reduce((sum, p) => sum + p.weight, 0);

// ============================================================================
// SPIN THE WHEEL (Client-side selection, server validates)
// ============================================================================

/**
 * Spin the wheel and get a prize
 * This calls the Cloud Function for secure prize distribution
 */
export async function spinWheel(userId: string): Promise<SpinResult> {
  try {
    console.log('🎡 [Wheel] Spinning wheel for user:', userId.substring(0, 8));
    
    // Call cloud function for secure spin
    const spinWheelFn = httpsCallable<
      Record<string, never>,
      SpinResult
    >(functions, 'spinDailyWheel');

    const result = await spinWheelFn({});
    
    if (result.data.success) {
      console.log('🎡 [Wheel] Won:', result.data.prize?.label);
    }
    
    return result.data;

  } catch (error) {
    console.error('❌ [Wheel] Spin error:', error);
    return {
      success: false,
      prize: null,
      finalValue: 0,
      streakBonus: 0,
      vipMultiplier: 1,
      message: 'Failed to spin. Please try again.',
      nextSpinTime: null,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Local spin for testing/fallback
 * WARNING: Not secure - use Cloud Function in production
 */
export async function spinWheelLocal(userId: string): Promise<SpinResult> {
  try {
    // Check if user can spin
    const status = await getSpinStatus(userId);
    
    if (!status.canSpin) {
      return {
        success: false,
        prize: null,
        finalValue: 0,
        streakBonus: 0,
        vipMultiplier: 1,
        message: 'You already spun today! Come back tomorrow.',
        nextSpinTime: status.nextSpinTime,
      };
    }

    // Select prize based on weights
    const prize = selectPrize();
    
    // Calculate bonuses
    const streakBonus = calculateStreakBonus(status.currentStreak + 1);
    const vipMultiplier = 1; // TODO: Get from user profile
    
    // Calculate final value
    let finalValue = prize.value;
    if (prize.type === 'playcoins' || prize.type === 'jackpot') {
      finalValue = Math.floor(prize.value * (1 + streakBonus) * vipMultiplier);
    }

    // Update spin record
    const spinRef = doc(db, 'wheelSpins', userId);
    const now = Date.now();
    const tomorrow = getNextMidnight();
    
    await setDoc(spinRef, {
      userId: userId,
      lastSpinTime: now,
      currentStreak: status.currentStreak + 1,
      totalSpins: status.totalSpins + 1,
      totalWon: status.totalWon + finalValue,
      lastPrizeId: prize.id,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    // TODO: Add PlayCoins to wallet via Cloud Function
    
    return {
      success: true,
      prize,
      finalValue,
      streakBonus,
      vipMultiplier,
      message: getMessage(prize, finalValue),
      nextSpinTime: tomorrow,
    };

  } catch (error) {
    console.error('❌ [Wheel] Local spin error:', error);
    return {
      success: false,
      prize: null,
      finalValue: 0,
      streakBonus: 0,
      vipMultiplier: 1,
      message: 'Something went wrong. Please try again.',
      nextSpinTime: null,
    };
  }
}

// ============================================================================
// GET SPIN STATUS
// ============================================================================

/**
 * Check if user can spin and get their stats
 */
export async function getSpinStatus(userId: string): Promise<SpinStatus> {
  try {
    // Read status from the SERVER (getDailyWheelStatus callable). The
    // wheelSpins collection is server-owned - the spin callable writes it -
    // so reading it straight from the browser is denied by the security
    // rules and the button would render a wrong state. The server has the
    // doc and the same eligibility maths, so it answers instead.
    const statusFn = httpsCallable<Record<string, never>, SpinStatus>(
      functions, 'getDailyWheelStatus'
    );
    const result = await statusFn({});
    return result.data;
  } catch (error) {
    console.error('❌ [Wheel] Status error:', error);
    // Fail OPEN so the wheel is never wrongly locked: the spin callable is
    // the real once-per-day gate and answers "come back tomorrow" itself.
    return {
      canSpin: true,
      lastSpinTime: null,
      nextSpinTime: null,
      currentStreak: 0,
      totalSpins: 0,
      totalWon: 0,
    };
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Select a prize based on weights
 */
function selectPrize(): WheelPrize {
  const random = Math.random() * TOTAL_WEIGHT;
  let cumulative = 0;
  
  for (const prize of WHEEL_PRIZES) {
    cumulative += prize.weight;
    if (random <= cumulative) {
      return prize;
    }
  }
  
  // Fallback to first prize
  return WHEEL_PRIZES[0];
}

/**
 * Calculate streak bonus percentage
 * Day 1: 0%, Day 2: 5%, Day 3: 10%, etc. (max 50%)
 */
function calculateStreakBonus(streak: number): number {
  if (streak <= 1) return 0;
  return Math.min((streak - 1) * 0.05, 0.5);
}

/**
 * Get midnight timestamp for next day
 */
function getNextMidnight(fromTime?: number): number {
  const date = new Date(fromTime || Date.now());
  date.setDate(date.getDate() + 1);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

/**
 * Get celebration message for prize
 */
function getMessage(prize: WheelPrize, finalValue: number): string {
  switch (prize.type) {
    case 'jackpot':
      return ` JACKPOT! You won ${finalValue.toLocaleString()} PlayCoins!`;

    case 'pkcash':
      // Real, redeemable money - never label it PlayCoins.
      return `💵 You won $${finalValue} PSS Coins!`;

    case 'multiplier':
      return ` ${prize.value}X Multiplier! Your next win is doubled!`;

    case 'playcoins':
      return `${prize.emoji} You won ${finalValue.toLocaleString()} PlayCoins!`;
    default:
      return `You won ${prize.label}!`;
  }
}

/**
 * Get prize index for wheel animation
 */
export function getPrizeIndex(prizeId: string): number {
  return WHEEL_PRIZES.findIndex(p => p.id === prizeId);
}

/**
 * Format time until next spin
 */
export function formatTimeUntilSpin(nextSpinTime: number): string {
  const now = Date.now();
  const diff = nextSpinTime - now;
  
  if (diff <= 0) return 'Now!';
  
  const hours = Math.floor(diff / (60 * 60 * 1000));
  const minutes = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000));
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

// ============================================================================
// SERVICE EXPORT
// ============================================================================

const wheelSpinService = {
  // Core
  spinWheel,
  spinWheelLocal,
  getSpinStatus,
  
  // Helpers
  getPrizeIndex,
  formatTimeUntilSpin,
  
  // Config
  WHEEL_PRIZES,
};

export default wheelSpinService;